import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Globe, Target, X, Check } from "lucide-react";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import Eyebrow from "@/components/site/Eyebrow";
import { ACCENT, NAVY, CREAM } from "@/lib/siteTheme";
import heronIcon from "@/assets/design/blue-heron-icon.webp";
import phoneRing from "@/assets/design/phone-ring.webp";
import googleBadge from "@/assets/design/google-icon.webp";
import { reviews } from "@/lib/reviews";

const trustStats = [
  { value: "5.0★", label: "Google Rating", divider: true },
  { value: "10+", label: "Years in Marketing", divider: true },
  { value: "KC", label: "Based, Local Focus", divider: false },
];

const pillars = [
  {
    tag: "Foundation",
    title: "Websites & Digital Presence",
    desc: "A fast, mobile-first site built to convert visitors into calls and booked jobs, not just look good.",
    icon: Globe,
    featured: true,
  },
  {
    tag: "Visibility",
    title: "Local SEO & Google Ads",
    desc: "Own the map pack and the paid results, so you show up the moment someone nearby is ready to buy.",
    icon: Target,
    featured: false,
  },
];

const processSteps = [
  { n: "01", title: "Free Consultation", desc: "A 30-minute call to understand your business and goals." },
  { n: "02", title: "Custom Strategy", desc: "A plan built around your market, not a template." },
  { n: "03", title: "Execution", desc: "I build and run the campaigns, hands-on." },
  { n: "04", title: "Measurable Growth", desc: "Clear reporting tied to leads and revenue.", final: true },
];

const genericPoints = [
  "A cookie-cutter strategy applied to every client",
  "A junior account manager you rarely hear from",
  "Vague monthly reports full of vanity metrics",
  "A 12-month contract signed before any results",
];

const heronPoints = [
  "A strategy built around your specific market and goals",
  "Direct access to the person doing the work",
  "Reporting tied to leads, calls, and revenue",
  "No long-term contract. You stay because it works",
];

const Index = () => {
  const [scrolled, setScrolled] = useState(false);
  const [showNotif, setShowNotif] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    let seen = false;
    try {
      seen = sessionStorage.getItem("bhNotifSeen") === "1";
    } catch {
      // sessionStorage unavailable, treat as unseen
    }
    if (seen) return;
    const timer = setTimeout(() => {
      try {
        sessionStorage.setItem("bhNotifSeen", "1");
      } catch {
        // ignore
      }
      setShowNotif(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const visibleReviews = reviews.slice(0, 9);

  return (
    <Layout active="home" background={NAVY}>
      <div style={{ fontFamily: "'Archivo', sans-serif", background: NAVY }} className="bh-home w-full antialiased">
        <Seo
          title="Blue Heron Marketing | Kansas City Digital Marketing for Local Businesses"
          description="Marketing that makes the phone ring. Websites, local SEO, Google Ads, and fractional CMO services for local businesses, built in Kansas City."
          path="/"
        />

        {showNotif && (
          <Link
            to="/book"
            className="fixed top-[22px] right-[22px] z-[90] flex items-start gap-3 w-[372px] max-w-[calc(100vw-32px)] rounded-[20px] px-4 py-3.5 no-underline"
            style={{
              background: "rgba(244,241,234,0.94)",
              backdropFilter: "blur(18px)",
              WebkitBackdropFilter: "blur(18px)",
              boxShadow: "0 18px 44px rgba(0,0,0,.28)",
              animation: "bhNotifIn .5s cubic-bezier(.16,.9,.3,1) both",
            }}
          >
            <span className="relative flex-none w-[42px] h-[42px]">
              <span className="flex items-center justify-center w-[42px] h-[42px] rounded-full overflow-hidden" style={{ background: NAVY }}>
                <img src={heronIcon} alt="" width={500} height={366} className="w-[26px] h-auto block brightness-0 invert" />
              </span>
              <span
                className="absolute -right-1 -bottom-1 w-[22px] h-[22px] rounded-md flex items-center justify-center"
                style={{ background: "#43CC47", boxShadow: "0 1px 3px rgba(0,0,0,.25)" }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="#fff"><path d="M12 3C6.9 3 2.8 6.3 2.8 10.4c0 2.3 1.3 4.4 3.4 5.7-.2.9-.8 2.3-1.6 3.2 0 0 2.6-.4 4.4-1.7.9.2 1.9.4 3 .4 5.1 0 9.2-3.3 9.2-7.6S17.1 3 12 3z"/></svg>
              </span>
            </span>
            <span className="flex-1 min-w-0 flex flex-col gap-0.5">
              <span className="flex items-baseline justify-between gap-2.5">
                <span className="font-bold text-[14.5px] tracking-[-0.01em] whitespace-nowrap" style={{ color: "#11212F" }}>New Blue Heron Lead</span>
                <span className="text-xs whitespace-nowrap" style={{ color: "rgba(17,33,47,0.5)" }}>now</span>
              </span>
              <span className="text-[14.5px] leading-snug" style={{ color: "rgba(17,33,47,0.78)" }}>Need more leads? Click here.</span>
            </span>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowNotif(false);
              }}
              className="absolute -top-2 -right-2 w-6 h-6 rounded-full border-none flex items-center justify-center p-0"
              style={{ background: "rgba(17,33,47,0.75)", color: "#fff" }}
            >
              <X className="w-3 h-3" />
            </button>
          </Link>
        )}

        {/* Hero */}
        <section
          id="top"
          className="relative overflow-hidden px-6 md:px-14 pt-[170px] pb-[130px] flex flex-col justify-center box-border"
          style={{ background: NAVY, minHeight: "calc(100vh + 76px)" }}
        >
          <div
            className="absolute pointer-events-none"
            style={{
              top: "-25%",
              right: "-10%",
              width: 900,
              height: 900,
              background: "radial-gradient(ellipse at center, rgba(60,110,190,0.16), transparent 65%)",
            }}
          />
          <div className="absolute pointer-events-none" style={{ right: -80, top: 170, width: 480, maxWidth: "46vw", opacity: 0.06, transform: "scaleX(-1)" }}>
            <img src={heronIcon} alt="" width={500} height={366} className="w-full h-auto block brightness-0 invert" />
          </div>
          <div className="max-w-[1280px] w-full mx-auto relative z-10 box-border">
            <h1
              className="font-black leading-[0.98] tracking-[-0.025em] mb-7 max-w-[20ch]"
              style={{ color: "#F7F5F0", fontSize: "clamp(48px,7.2vw,110px)" }}
            >
              Marketing that<br />makes the<br />phone ring.
              <img
                src={phoneRing}
                alt=""
                className="inline-block align-[-0.12em] ml-[0.18em] brightness-0 invert"
                style={{ width: "1em", height: "1em", animation: "bhRing 1.5s ease-in-out infinite", transformOrigin: "50% 70%" }}
              />
            </h1>
            <p className="max-w-[560px] mb-7 leading-relaxed" style={{ color: "rgba(244,241,234,0.62)", fontSize: "clamp(17px,1.5vw,20px)" }}>
              No fluff. No long-term contracts. Just strategy, execution, and results you can measure, built for owners who are done guessing.
            </p>
            <a
              href="https://maps.app.goo.gl/q3Hk61Px8k53Yxwi9"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white rounded-full py-2 pl-2.5 pr-4 mb-7 no-underline font-extrabold text-[13px] tracking-[0.05em] uppercase whitespace-nowrap"
              style={{ color: "#081627" }}
            >
              <img src={googleBadge} alt="" width={94} height={96} className="w-4 h-4 block" />
              5 Star Rated on Google
            </a>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/book"
                className="inline-flex items-center gap-2.5 font-extrabold text-base px-[30px] py-[17px] rounded-full no-underline whitespace-nowrap transition-transform hover:-translate-y-0.5"
                style={{ background: ACCENT, color: "#081627" }}
              >
                Book a Free Call →
              </Link>
              <Link
                to="/case-studies"
                className="inline-flex items-center gap-2.5 font-bold text-base px-7 py-[17px] rounded-full no-underline whitespace-nowrap transition-colors"
                style={{ color: "#F4F1EA", border: "1px solid rgba(244,241,234,.22)" }}
              >
                View Case Studies
              </Link>
            </div>
          </div>
        </section>

        {/* Trust strip */}
        <section className="px-6 md:px-14 py-14" style={{ background: NAVY, borderTop: `3px solid ${ACCENT}` }}>
          <div className="max-w-[1280px] mx-auto flex flex-wrap items-center justify-center gap-14">
            {trustStats.map((stat) => (
              <div key={stat.label} className="flex items-center gap-14">
                <div className="flex items-baseline gap-3">
                  <span className="font-black leading-none tracking-[-0.02em]" style={{ color: ACCENT, fontSize: "clamp(40px,5.2vw,64px)" }}>
                    {stat.value}
                  </span>
                  <span className="font-bold text-[15px] tracking-[0.02em]" style={{ color: "rgba(244,241,234,0.65)" }}>{stat.label}</span>
                </div>
                {stat.divider && <span className="w-px h-10 inline-block" style={{ background: "rgba(244,241,234,0.15)" }} />}
              </div>
            ))}
          </div>
        </section>

        {/* Services */}
        <section id="services" className="px-6 md:px-14 py-[130px] scroll-mt-20" style={{ background: CREAM }}>
          <div className="max-w-[1280px] mx-auto">
            <div className="mb-14 max-w-[640px]">
              <Eyebrow>What I Do</Eyebrow>
              <h2 className="font-black leading-[0.98] tracking-[-0.025em] mb-3" style={{ color: NAVY, fontSize: "clamp(38px,5.6vw,68px)" }}>
                Two ways I grow your business.
              </h2>
              <p className="text-[17px] leading-relaxed" style={{ color: "rgba(14,16,21,0.55)" }}>Pick one, or run both as a full-funnel system.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
              {pillars.map((p) => {
                const Icon = p.icon;
                return (
                  <div
                    key={p.title}
                    className="relative overflow-hidden rounded-[20px] flex flex-col"
                    style={p.featured ? { background: NAVY, padding: "48px 40px" } : { background: "#fff", border: "1px solid rgba(14,16,21,.08)", padding: "40px 36px" }}
                  >
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center mb-7 relative z-10"
                      style={p.featured ? { background: ACCENT, color: "#081627" } : { background: "rgba(232,163,61,0.14)", color: "#9c5e15" }}
                    >
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="font-extrabold uppercase relative z-10 mb-4" style={{ color: p.featured ? ACCENT : "#9c5e15", fontSize: p.featured ? 13 : 12.5, letterSpacing: "0.1em" }}>
                      {p.tag}
                    </div>
                    <h3 className="font-black relative z-10 mb-3.5" style={{ color: p.featured ? "#F7F5F0" : NAVY, fontSize: p.featured ? 29 : 24, lineHeight: 1.2, letterSpacing: "-0.015em" }}>
                      {p.title}
                    </h3>
                    <p className="relative z-10" style={{ color: p.featured ? "rgba(244,241,234,0.6)" : "rgba(14,16,21,0.6)", fontSize: p.featured ? 15.5 : 15, lineHeight: 1.6 }}>
                      {p.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="relative overflow-hidden px-6 md:px-14 pt-[150px] pb-[130px]" style={{ background: NAVY }}>
          <div className="absolute pointer-events-none" style={{ left: -160, bottom: -100, width: 460, maxWidth: "46vw", opacity: 0.05 }}>
            <img src={heronIcon} alt="" width={500} height={366} className="w-full h-auto block brightness-0 invert" />
          </div>
          <div className="max-w-[1280px] mx-auto relative z-10">
            <div className="mb-20 max-w-[640px]">
              <Eyebrow dark>How It Works</Eyebrow>
              <h2 className="font-black leading-[0.98] tracking-[-0.025em]" style={{ color: "#F7F5F0", fontSize: "clamp(38px,5.6vw,68px)" }}>
                From call to measurable growth.
              </h2>
            </div>
            <div className="flex flex-col md:flex-row items-start relative gap-7">
              <div
                className="hidden md:block absolute top-7 left-7 right-7 h-[3px] z-0"
                style={{ background: `linear-gradient(90deg, rgba(232,163,61,0.15), ${ACCENT}, rgba(232,163,61,0.15))` }}
              />
              {processSteps.map((step, i) => (
                <div
                  key={step.n}
                  className="relative z-10 flex-1 flex flex-col items-center text-center px-3"
                  style={{ marginTop: i % 2 === 1 ? 32 : 0 }}
                >
                  <div
                    className="w-14 h-14 rounded-full font-black text-lg flex items-center justify-center mb-[22px]"
                    style={
                      step.final
                        ? { background: ACCENT, color: "#081627" }
                        : { background: NAVY, border: `3px solid ${ACCENT}`, color: ACCENT }
                    }
                  >
                    {step.n}
                  </div>
                  <div className="rounded-2xl px-[22px] py-[26px] w-full" style={{ background: "rgba(244,241,234,0.04)", border: "1px solid rgba(244,241,234,.1)" }}>
                    <h3 className="font-extrabold text-[19px] mb-2.5" style={{ color: "#F7F5F0", letterSpacing: "-0.01em" }}>{step.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: "rgba(244,241,234,0.55)" }}>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section id="reviews" className="px-6 md:px-14 py-[130px] scroll-mt-20" style={{ background: CREAM }}>
          <div className="max-w-[1280px] mx-auto">
            <div className="mb-14 max-w-[640px]">
              <Eyebrow>Proof, Not Promises</Eyebrow>
              <h2 className="font-black leading-[0.98] tracking-[-0.025em] mb-3" style={{ color: NAVY, fontSize: "clamp(38px,5.6vw,68px)" }}>
                What clients actually say.
              </h2>
              <p className="text-[17px] leading-relaxed" style={{ color: "rgba(14,16,21,0.55)" }}>Real reviews, unedited, Blue Heron is rated 5.0 on Google.</p>
            </div>
            <div className="relative">
              <div className="columns-1 md:columns-2 lg:columns-3 gap-6">
                {visibleReviews.map((r) => (
                  <div
                    key={r.name}
                    className="break-inside-avoid bg-white rounded-2xl px-[26px] pt-[26px] pb-7 mb-6"
                    style={{ border: "1px solid rgba(14,16,21,.07)", boxShadow: "0 12px 30px rgba(14,38,68,.06)" }}
                  >
                    <div className="flex items-center justify-between mb-3.5">
                      <div className="flex items-center gap-2.5">
                        <img src={googleBadge} alt="" width={94} height={96} className="w-[22px] h-[22px] block" />
                        <span className="font-extrabold text-[15px]" style={{ color: NAVY }}>{r.name}</span>
                      </div>
                      <span style={{ color: ACCENT, fontSize: 13, letterSpacing: "1px" }}>★★★★★</span>
                    </div>
                    <p className="text-[14.5px] leading-relaxed" style={{ color: "rgba(14,16,21,0.7)" }}>{r.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center mt-10">
              <Link
                to="/reviews"
                className="inline-flex items-center gap-2.5 font-extrabold text-[15px] px-[34px] py-4 rounded-full no-underline transition-transform hover:-translate-y-0.5"
                style={{ background: NAVY, color: "#F7F5F0", fontFamily: "inherit" }}
              >
                Show All Reviews
              </Link>
            </div>
          </div>
        </section>

        {/* The Difference */}
        <section className="relative overflow-hidden px-6 md:px-14 py-[130px]" style={{ background: NAVY }}>
          <div className="absolute pointer-events-none" style={{ right: -120, top: 40, width: 440, maxWidth: "42vw", opacity: 0.06, transform: "scaleX(-1)" }}>
            <img src={heronIcon} alt="" width={500} height={366} className="w-full h-auto block brightness-0 invert" />
          </div>
          <div className="max-w-[1280px] mx-auto relative z-10">
            <div className="mb-16 max-w-[760px]">
              <Eyebrow dark>The Difference</Eyebrow>
              <h2 className="font-black leading-[0.98] tracking-[-0.025em] mb-3" style={{ fontSize: "clamp(38px,5.6vw,68px)" }}>
                <span style={{ color: "rgba(244,241,234,0.38)" }}>Big agencies hand you off.</span>
                <br />
                <span style={{ color: ACCENT }}>I stay hands-on.</span>
              </h2>
              <p className="text-[17px] leading-relaxed" style={{ color: "rgba(244,241,234,0.55)" }}>Here's what changes when you stop hiring by the checklist.</p>
            </div>
            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
              <div
                className="hidden md:flex absolute top-1/2 left-1/2 z-[3] w-14 h-14 rounded-full items-center justify-center font-extrabold text-xs"
                style={{
                  transform: "translate(-50%,-50%)",
                  background: NAVY,
                  border: "2px solid rgba(244,241,234,.2)",
                  color: "rgba(244,241,234,.55)",
                  letterSpacing: "0.05em",
                  boxShadow: `0 0 0 8px ${NAVY}`,
                }}
              >
                VS
              </div>
              <div className="rounded-2xl px-11 py-12" style={{ background: "rgba(244,241,234,0.05)", border: "1px solid rgba(244,241,234,.16)" }}>
                <div className="font-extrabold text-[12.5px] tracking-[0.1em] uppercase mb-7" style={{ color: "rgba(244,241,234,0.45)" }}>The Generic Agency Way</div>
                <div className="flex flex-col gap-5.5">
                  {genericPoints.map((pt) => (
                    <div key={pt} className="flex gap-3.5 items-start">
                      <X className="w-[26px] h-[26px] flex-none mt-0.5" style={{ color: "rgba(244,241,234,0.4)" }} strokeWidth={2} />
                      <span className="text-[15.5px] leading-snug" style={{ color: "rgba(244,241,234,0.6)" }}>{pt}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl px-11 pt-[52px] pb-[52px]" style={{ background: ACCENT, transform: "translateY(-24px)", boxShadow: "0 30px 70px rgba(0,0,0,0.35)" }}>
                <div className="flex items-center gap-2.5 mb-7">
                  <span className="font-extrabold text-[12.5px] tracking-[0.1em] uppercase" style={{ color: "rgba(14,38,68,0.55)" }}>The Blue Heron Way</span>
                  <span className="font-extrabold text-[10.5px] tracking-[0.08em] uppercase px-2.5 py-1 rounded-full" style={{ background: NAVY, color: ACCENT }}>Recommended</span>
                </div>
                <div className="flex flex-col gap-5.5">
                  {heronPoints.map((pt) => (
                    <div key={pt} className="flex gap-3.5 items-start">
                      <Check className="w-[26px] h-[26px] flex-none mt-0.5" style={{ color: NAVY }} strokeWidth={2.5} />
                      <span className="font-semibold text-[15.5px] leading-snug" style={{ color: NAVY }}>{pt}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="relative overflow-hidden px-6 md:px-14 py-[150px] text-center" style={{ background: CREAM }}>
          <div className="absolute pointer-events-none" style={{ left: -120, bottom: -80, width: 420, maxWidth: "40vw", opacity: 0.05 }}>
            <img src={heronIcon} alt="" width={500} height={366} className="w-full h-auto block" />
          </div>
          <div className="max-w-[760px] mx-auto relative z-10">
            <div className="flex flex-col items-center mb-7">
              <div className="font-extrabold text-[13px] tracking-[0.16em] uppercase mb-3" style={{ color: "#9c5e15" }}>Let's Talk</div>
              <div className="w-8 h-[3px] rounded-sm" style={{ background: ACCENT }} />
            </div>
            <h2 className="font-black leading-[0.98] tracking-[-0.025em] mb-5" style={{ color: NAVY, fontSize: "clamp(38px,5.6vw,68px)" }}>
              Ready to stop guessing?
            </h2>
            <p className="mb-10 text-lg leading-relaxed" style={{ color: "rgba(14,16,21,0.6)" }}>
              Book a free 30-minute consultation. No pressure, no sales pitch. Just an honest conversation about your goals.
            </p>
            <div className="flex flex-wrap gap-3.5 justify-center items-center mb-10">
              <Link
                to="/book"
                className="inline-flex items-center gap-2.5 font-extrabold text-lg px-[38px] py-5 rounded-full no-underline whitespace-nowrap transition-transform hover:-translate-y-0.5"
                style={{ background: ACCENT, color: "#081627" }}
              >
                Book A Free Call →
              </Link>
              <a
                href="tel:+18163704263"
                className="inline-flex items-center gap-2.5 font-bold text-lg px-[34px] py-5 rounded-full no-underline whitespace-nowrap transition-colors"
                style={{ color: NAVY, border: "1px solid rgba(14,38,68,.2)" }}
              >
                (816) 370-4263
              </a>
            </div>
            <div className="flex flex-wrap gap-6 justify-center font-semibold text-sm" style={{ color: "rgba(14,16,21,0.55)" }}>
              <span className="inline-flex items-center gap-2"><span style={{ color: "#9c5e15" }}>✓</span>Free 30-minute call</span>
              <span className="inline-flex items-center gap-2"><span style={{ color: "#9c5e15" }}>✓</span>No pressure, no pitch</span>
              <span className="inline-flex items-center gap-2"><span style={{ color: "#9c5e15" }}>✓</span>Reply within one business day</span>
            </div>
          </div>
        </section>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .bh-home h1, .bh-home h2, .bh-home h3, .bh-home h4 {
          font-family: 'Archivo', sans-serif;
        }
        @keyframes bhNotifIn {
          from { opacity: 0; transform: translateX(120%); }
          to { opacity: 1; transform: none; }
        }
        @keyframes bhRing {
          0%, 56%, 100% { transform: rotate(0deg); }
          58% { transform: rotate(-15deg); }
          60% { transform: rotate(15deg); }
          62% { transform: rotate(-12deg); }
          64% { transform: rotate(12deg); }
          66% { transform: rotate(-8deg); }
          68% { transform: rotate(8deg); }
          70% { transform: rotate(0deg); }
        }
      ` }} />
    </Layout>
  );
};

export default Index;
